import React from "react"
import i18next from "i18next"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"

import { useParams } from "react-router-dom"
import ProtectedLayout from "layouts/Protected"
import { privateRoutes } from "router/routes"

import { useAlert, useAuth, useBikeStore } from "hooks"

import dateService from "services/date"
import { CaretIcon } from "components/Elements/Icons"
import { FormSearchBike } from "./Partials"

// PDF
import { pdf } from "@react-pdf/renderer"
import saveBlob from "utils/saveBlob"
import SummaryBikePDF from "features/bike/Partials/Pdf/SummaryBikePDF"

import { getLabelFromBikeId } from "utils/constants"

const updatesVariants = [
    {
        id: "sell-succcess",
        title: i18next.t("sell_done_title", { ns: "bike" }),
        successDescription: i18next.t("sell_success_description", { ns: "bike" })
    }
]

export const BikeUpdatedPage = ({ variant = updatesVariants[0].id }) => {
    const {
        currentBike: bike,
        fetchBike
    } = useBikeStore()
    const [operatorGender, setOperatorGender] = React.useState(" ")
    const { t } = useTranslation()

    const { slug } = useParams()
    const { alert } = useAlert()
    const { entity } = useAuth()

    React.useEffect(() => {
        !bike && fetchBike(slug)
    },[bike]) // eslint-disable-line

    React.useEffect(() => {
        if (bike?.operator && entity?.data_operators) {
            const gender = entity.data_operators.find(b => b.operator === bike.operator)?.entity_data?.operator_gender

            if(gender && gender !== "undefinded") setOperatorGender(gender)
        }
    }, [bike, entity]) // eslint-disable-line

    // Generate PDF summary
    const viewSummary = React.useCallback(async () => {
        // Manage pdf generation
        const timeStamp = dateService.getCurrentDate("dd-MM-y-HH'h'mm")
        const generateBikePDF = bike && operatorGender && await pdf((
                <SummaryBikePDF
                    bike={bike}
                    entity={entity}
                    operatorGender={operatorGender}
                />
            ))
            .toBlob()
            .then((blob) => {
                saveBlob(blob, `imprimer-recapitulatif-${bike?.code}-${timeStamp}.pdf`, { targetFile: false })
                return blob
            })
            .catch((e) => null)

        if (!generateBikePDF) {
            alert("error", t("pdf.pdf_error", { ns: "bike" }))
        }
    }, [bike, entity, operatorGender, alert, t])

    return (
        <ProtectedLayout className="p-12">
            <div className="l-container">
                {bike &&
                    <>
                        <div className="c-section-title mb-12">
                            <div className="c-section-title__content justify-between">
                                <div className="flex items-center justify-start w-full">
                                    <NavLink to={privateRoutes.searchBike.path} className="flex" aria-label={t("back_to_search", { ns: "bike" })}>
                                        <span className="flex py-2" style={{ transform: "rotate(180deg)" }}><CaretIcon size={30} /></span>
                                    </NavLink>

                                    <h1 className="c-section-title__title c-h2 text-primary font-medium ml-5">
                                        {updatesVariants.find(v => v.id === variant)?.title}
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div className="c-card p-5 lg:px-12 lg:py-7 mb-7">
                            <div className="flex justify-between items-start">
                                <div className="lg:w-1/2 w-full">
                                    <FormSearchBike readonly={true} />
                                </div>
                                <div className="lg:w-1/2 w-full flex justify-end">
                                    <button
                                        type="button"
                                        className="c-btn c-btn--primary-bordered px-12 mb-7"
                                        onClick={viewSummary}
                                    >
                                        {t("cta_print_recap", { ns: "bike" })}
                                    </button>
                                </div>
                            </div>
                            <p className="mt-7">
                                {updatesVariants.find(v => v.id === variant)?.successDescription}
                            </p>
                        </div>

                        <div className="l-grid u-mg-negative-hz-m mb-7">
                            <div className="lg:w-1/2 w-full p-5">
                                <div className="c-card h-full p-5 lg:px-12 lg:py-7 mb-7">
                                    <h2 className="c-h2 font-semibold mb-7">
                                        {t("description_title", { ns: "bike" })}
                                    </h2>
                                    <p className="mb-2">
                                        <span className="text-black font-bold">{t("brand", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{bike?.bikeBrand}</span>
                                    </p>
                                    <p className="mb-2">
                                        <span className="text-black font-bold">{t("model", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{bike?.bikeModel}</span>
                                    </p>
                                    <p className="mb-2">
                                        <span className="text-black font-bold">{t("type", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{t(`${getLabelFromBikeId(bike?.bikeTypeId)}`)}</span>
                                    </p>
                                    <p className="mb-2">
                                        <span className="text-black font-bold">{t("color", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{bike?.bikeColor}</span>
                                    </p>
                                    <p className="mb-7">
                                        <span className="text-black font-bold">{t("label_vae_short", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{bike?.isVAE === "true" ? t("label_vae_true", { ns: "bike" }) : t("label_vae_false", { ns: "bike" })}</span>
                                    </p>
                                    <p className="mb-2">
                                        <span className="text-black font-bold">{t("serial", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{bike?.bikeSerial}</span>
                                    </p>
                                    {bike?.isVAE === "true" &&
                                        <>
                                            <p className="mb-2">
                                                <span className="text-black font-bold">{t("serial_engine", { ns: "bike" })} : </span>
                                                <span className="text-primary ml-2">{bike?.bikeEngineSerial}</span>
                                            </p>
                                            <p className="mb-2">
                                                <span className="text-black font-bold">{t("serial_battery", { ns: "bike" })} : </span>
                                                <span className="text-primary ml-2">{bike?.bikeBatterySerial}</span>
                                            </p>
                                        </>
                                    }
                                </div>
                            </div>
                            <div className="lg:w-1/2 w-full p-5">
                                <div className="c-card h-full p-5 lg:px-12 lg:py-7 mb-7">
                                    <h2 className="c-h2 font-semibold mb-7">
                                        {t("owner_title", { ns: "bike" })}
                                    </h2>
                                    {parseInt(bike?.owner?.ownerGender) === 1 &&
                                    <>
                                        <p className="mb-2">
                                            <span className="text-black font-bold">{t("owner.name", { ns: "bike" })} : </span>
                                            <span className="text-primary ml-2">{bike?.owner.ownerName}</span>
                                        </p>
                                        <p className="mb-2">
                                            <span className="text-black font-bold">{t("owner.firstname", { ns: "bike" })} : </span>
                                            <span className="text-primary ml-2">{bike?.owner.ownerFirstname}</span>
                                        </p>
                                    </>
                                    }
                                    {parseInt(bike?.owner?.ownerGender) === 2 &&
                                    <p className="mb-2">
                                        <span className="text-black font-bold">{t("owner.raison_sociale", { ns: "bike" })} : </span>
                                        <span className="text-primary ml-2">{bike?.owner.ownerRaisonSociale}</span>
                                    </p>
                                    }

                                    {bike?.owner?.ownerPhonenumber &&
                                        <p className="mb-2">
                                            <span className="text-black font-bold">{t("owner.phonenumber", { ns: "bike" })} : </span>
                                            <span className="text-primary ml-2">{bike?.owner.ownerPhonenumber.replace("+33", "0")}</span>
                                        </p>
                                    }

                                    {bike?.owner?.mail &&
                                        <p className="mb-2">
                                            <span className="text-black font-bold">{t("owner.mail", { ns: "bike" })} : </span>
                                            <span className="text-primary ml-2">{bike.owner.mail}</span>
                                        </p>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-start mt-7 mb-5">
                            <NavLink
                                to={privateRoutes.searchBike.path}
                                className="c-btn c-btn--primary px-12 mb-7"
                            >
                                {t("cta_activate_new_bike", { ns: "bike" })}
                            </NavLink>
                        </div>
                    </>
                }
            </div>
        </ProtectedLayout>
    )
}
